import React from 'react';
import { Link } from 'react-router-dom';
import './StaticLegalPages.css'; // Shared CSS for all legal text pages

const TermsPage = () => {
  return (
    <div className="static-page-container">
      
      {/* Header Section */}
      <div className="terms-header">
          <h1>Terms & Conditions</h1>
          <p className="last-updated"><strong>Last Updated:</strong> January 20, 2026</p>
      </div>

      {/* --- NOTICE BOX --- */}
      <div className="policy-binding-notice">
        <p style={{margin: 0}}>
          <strong>Notice:</strong> By creating an account, signing up, or using FullMockup,
          you acknowledge that you have read, understood, and agree to be bound by these
          <strong> Terms & Conditions</strong> and our <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </div>

      <h2>1. Introduction</h2>
      <p>
        Welcome to <strong>FullMockup</strong> ("Company", "we", "our", "us"). These Terms and
        Conditions ("Terms") govern your use of our website, web application, and the
        proprietary physics-based mockup generation services (collectively, the "Service").
      </p>
      <p>
        By registering for an account, accessing, or using the Service, you ("User", "you")
        agree to be bound by these Terms. If you do not agree to these Terms, you must not
        access or use the Service.
      </p>
      <p>
        These Terms incorporate by reference our <Link to="/privacy">Privacy Policy</Link> and
        <Link to="/refund"> Refund Policy</Link>.
      </p>

      <h2>2. Service Description & Technology</h2>
      <p>
        FullMockup is a SaaS (Software as a Service) platform that allows users to generate
        photorealistic apparel mockups.
      </p>
      <ul>
        <li>
          <strong>Proprietary Engine:</strong> Unlike generic AI wrappers, we utilize a
          self-hosted, proprietary Physics-Based Warp Engine (V25) combined with computer
          vision technology to map 2D designs onto 3D surfaces.
        </li>
        <li>
          <strong>Self-Hosted Processing:</strong> All image processing is performed on our
          private infrastructure. We do not send your raw designs to third-party generative
          AI APIs (such as OpenAI or Replicate) for processing.
        </li>
        <li>
          <strong>Digital Goods:</strong> The Service creates digital image files ("Outputs")
          based on user-uploaded images ("Inputs").
        </li>
      </ul>

      <h2>3. Account Registration & Security</h2>
      <ul>
        <li>
          <strong>Eligibility:</strong> You must be at least 18 years old to use the Service.
        </li>
        <li>
          <strong>Credentials:</strong> You are responsible for maintaining the confidentiality
          of your login credentials. You are responsible for all activities that occur under
          your account.
        </li>
        <li>
          <strong>One Account Rule:</strong> Users may not create multiple "Starter" accounts
          to bypass daily credit limits. Detection of such activity will result in immediate
          permanent bans.
        </li>
      </ul>

      <h2>4. Subscriptions, Credits, and Billing</h2>
      <p>The Service operates on a Credit System. <strong>1 Generation = 1 Credit.</strong></p>
      
      <h3>4.1. Account Tiers</h3>
      <ul>
        <li>
          <strong>Starter (Free):</strong> Includes a limited daily allowance (e.g., 3 Credits/Day).
          These credits reset every 24 hours and do not roll over.
        </li>
        <li>
          <strong>Paid Tiers (Lite, Pro, Agency):</strong> Include a monthly allowance of credits.
          Unused credits do not roll over to the next month unless explicitly stated in your plan details.
        </li>
      </ul>

      <h3>4.2. Payments</h3>
      <ul>
        <li>
          <strong>Payment Gateway:</strong> All financial transactions are processed securely via
          <strong> Cashfree Payments</strong>. We do not store your credit card or banking details
          on our servers.
        </li>
        <li>
          <strong>Billing Cycle:</strong> Subscription fees are billed in advance on a monthly or
          annual basis.
        </li>
        <li>
          <strong>Taxes:</strong> Prices listed may be exclusive of applicable taxes (GST/VAT),
          which will be calculated at checkout based on your location.
        </li>
      </ul>

      <h2>5. Refund & Cancellation Policy</h2>
      <p>
        As FullMockup provides instant access to digital goods and utilizes server computation
        resources immediately:
      </p>
      <ul>
        <li>
          <strong>No Refunds on Used Credits:</strong> We generally do not offer refunds once
          credits have been utilized or a subscription month has commenced.
        </li>
        <li>
          <strong>Cancellation:</strong> You may cancel your subscription at any time via the
          "Billing" section of your dashboard. Your access will continue until the end of the
          current billing cycle, after which your account will revert to the Starter tier.
        </li>
        <li>
          <strong>Exceptions:</strong> If a technical error on our end results in a failed
          generation where credits were deducted but no image was produced, we will refund
          those specific credits to your account balance.
        </li>
      </ul>

      <h2>6. Data Retention & Storage Policy</h2>
      <p>
        To maintain high performance and low costs, we enforce strict data retention rules
        based on your tier. By using the Service, you acknowledge:
      </p>

      <h3>6.1. Starter (Free) Users</h3>
      <ul>
        <li>
          <strong>24-Hour Auto-Deletion:</strong> Input files (uploaded designs) and Output
          files (generated mockups) are automatically and permanently deleted from our servers
          24 hours after creation.
        </li>
        <li>
          <strong>Responsibility:</strong> It is your responsibility to download your generated
          mockups immediately. We cannot recover files once they are purged by our automated system.
        </li>
      </ul>

      <h3>6.2. Paid Users (Lite, Pro, Agency)</h3>
      <ul>
        <li>
          <strong>Permanent Storage:</strong> Your generated mockups are stored securely in our
          cloud storage for as long as your subscription remains active.
        </li>
        <li>
          <strong>Storage Limits:</strong> Each tier has a specific storage slot limit (e.g.,
          100 images for Pro). If you exceed this limit, you must delete old images or upgrade
          to generate new ones.
        </li>
      </ul>

      <h2>7. Intellectual Property Rights</h2>
      
      <h3>7.1. Your Content (User Assets)</h3>
      <p>
        You retain all ownership rights to the designs, logos, and images ("Input") you upload.
        You warrant that you own the copyright to your Inputs or have permission to use them.
      </p>

      <h3>7.2. Our Technology</h3>
      <p>
        FullMockup owns all rights, title, and interest in the Service, including the V25 Warp
        Engine, source code, user interface, and branding. You may not reverse-engineer, scrape,
        or copy our underlying technology.
      </p>

      <h3>7.3. Generated Outputs</h3>
      <ul>
        <li>
          <strong>Commercial Use:</strong> Subject to your compliance with these Terms, you are
          granted a perpetual, worldwide, royalty-free license to use the Output Images generated
          by the Service for personal or commercial purposes (e.g., selling T-shirts).
        </li>
        <li>
          <strong>No Copyright on AI Style:</strong> You own the final image composition, but
          you do not own the underlying 3D model or the apparel stock photo base used by the system.
        </li>
      </ul>

      <h2>8. Acceptable Use Policy</h2>
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>Upload content that is illegal, pornographic, hateful, or promotes violence.</li>
        <li>Upload content that infringes on the trademark or copyright of third parties (e.g., major brands like Nike/Disney without license).</li>
        <li>Attempt to bypass the credit system or "Ghostbuster" deletion protocols.</li>
        <li>Use automated scripts or bots to access the Service.</li>
      </ul>
      <p>
        <strong>Violation:</strong> We reserve the right to ban any account violating these
        rules without refund.
      </p>

      <h2>9. Limitation of Liability</h2>
      <p>
        The Service is provided "AS IS" and "AS AVAILABLE." While we strive for high-quality renders:
      </p>
      <ul>
        <li>We do not guarantee that the Service will be uninterrupted or error-free.</li>
        <li>
          We are not liable for data loss caused by the automated deletion policy for
          any users.
        </li>
        <li>
          In no event shall FullMockup's liability exceed the amount paid by you in the
          last 12 months.
        </li>
      </ul>

      <h2>10. Governing Law & Jurisdiction</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the <strong>laws of India</strong>.
        Any disputes arising out of or in connection with these Terms shall be subject to the
        exclusive jurisdiction of the courts located in <strong>New Delhi</strong>, India.
      </p>

      <h2>11. Contact Information</h2>
      <p>For questions regarding these Terms or the Service, please contact us:</p>
      
      <div className="contact-details-box">
        <p><strong>Email:</strong> support@fullmockup.com</p>
        <p><strong>Phone:</strong> +91 95606 95885</p>
        <p><strong>Address:</strong> R-53A, Sir Syed Road, Jamia Nagar, Okhla, New Delhi, India</p>
      </div>
      
      <p>
        Alternatively, you may use our <Link to="/contact-us">Official Contact Form</Link>.
      </p>
    </div>
  );
};

export default TermsPage;